import { Location } from "@angular/common";
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { Subscription } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { FormParams, PaginationDetails, TrainingForms } from "src/app/models/models";
import { GoogleAuthService } from "src/app/services/google-api.service";
import { DateRange } from "src/app/shared/constants/angular-datetime-picker.const";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { getFormTypeShortLabel } from "src/app/shared/utils/form-type-label";
import { filterOptionsByRole } from "src/app/shared/utils/form-types-visible.utils";
import { parseAocIdsFromQuery } from "src/app/util/aoc-filter-options.utils";

@Component({
	selector: "app-report",
	templateUrl: "./report.component.html",
	styleUrls: ["./report.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ReportComponent implements OnInit, OnDestroy, AfterViewInit {
	public columns!: Columns[];
	public configuration!: Config;
	isOnline: boolean = true;

	@ViewChild("trainingNameTpl", { static: true })
	trainingNameTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;
	@ViewChild("dateTpl", { static: true }) dateTpl!: TemplateRef<any>;
	// @ViewChild('statusTpl', { static: true }) statusTpl!: TemplateRef<any>;
	@ViewChild("formTypeTpl", { static: true }) formTypeTpl!: TemplateRef<any>;
	@ViewChild("aocTpl", { static: true }) aocTpl!: TemplateRef<any>;

	DATE_FORMAT = CONSTANT.DATE_FORMAT;

	filters?: FormParams = {
		page: this.constant.PAGINATION.DEFAULT_PAGE,
		size: this.constant.PAGINATION.DEFAULT_SIZE,
	};

	public pagination: Pagination = {
		limit: this.constant.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	formsSub!: Subscription;
	forms: TrainingForms[] = [];
	routeSub!: Subscription;

	startDate: Date = new Date(new Date().getFullYear(), new Date().getMonth() - 3, 1);

	endDate: Date = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);

	dateFilterEnabled = false;

	formTypes: string[] = [];

	selectedAocIds: string[] = [];
	searchQuery = "";
	private searchTimeout?: ReturnType<typeof setTimeout>;

	constructor(
		private sharedService: SharedService,
		private router: Router,
		private httpService: HttpService,
		private route: ActivatedRoute,
		private loaderService: LoaderService,
		private location: Location,
		private roleService: RoleService,
		private cdr: ChangeDetectorRef,
		private googleAuth: GoogleAuthService,
	) {
		const params = this.route.snapshot.queryParams;
		const hasDates = Boolean(params["startDate"] && params["endDate"]);
		this.dateFilterEnabled = hasDates;
		if (hasDates) {
			this.startDate = moment(params["startDate"]).toDate();
			this.endDate = moment(params["endDate"]).toDate();
		}
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.REPORT);
		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
			this.cdr.detectChanges();
		});

		this.initTableConfig();
	}

	ngAfterViewInit(): void {
		this.initParams();
	}

	ngOnDestroy(): void {
		if (this.formsSub) {
			this.formsSub.unsubscribe();
		}
		if (this.routeSub) {
			this.routeSub.unsubscribe();
		}
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	}

	initTableConfig() {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = this.constant.PAGINATION.MAX_PAGINATION_COUNT;

		this.columns = [
			...(this.isGroupAdmin
				? [
						{
							key: "aoc",
							title: "AOC",
							cellTemplate: this.aocTpl,
							width: "12%",
						},
					]
				: []),
			{
				key: "formTypeLabel",
				title: "Form Type",
				cellTemplate: this.formTypeTpl,
				width: "22%",
			},
			{
				key: "name",
				title: "Test Name",
				cellTemplate: this.trainingNameTpl,
				width: "28%",
			},
			{
				key: "updatedDate",
				title: "Submission Date/Time",
				cellTemplate: this.dateTpl,
				width: "22%",
			},
			{
				key: "",
				title: "Action",
				cellTemplate: this.actionTpl,
				width: "16%",
			},
		];
	}

	private buildListParams(): FormParams {
		const role = this.roleService.getUserRole();
		const params: FormParams = {
			...this.filters,
			role,
		};

		if (this.isGroupAdmin) {
			if (this.selectedAocIds.length) {
				params.aocIds = this.selectedAocIds;
			} else {
				delete params.aocIds;
			}
		} else {
			delete params.aocIds;
		}

		return params;
	}

	initParams(): void {
		this.routeSub = this.route.queryParams.subscribe((params) => {
			const hasDates = Boolean(params["startDate"] && params["endDate"]);
			this.dateFilterEnabled = hasDates;
			this.updateFilters({
				...this.filters,
				...params,
				...(!hasDates
					? {
							startDate: undefined,
							endDate: undefined,
						}
					: {}),
			});
		});
	}

	initFormList(): void {
		if (!this.isOnline) return;
		this.loaderService.setLoading(true);

		// filter by userId if role is pilot
		// if ( this.roleService.isPilot ) {
		//   this.filters = {
		//     ...this.filters,
		//     userId: this.roleService?.userDetails?.id,
		//   }
		// }

		const role = this.roleService.getUserRole();

		this.formsSub?.unsubscribe();
		this.formsSub = this.httpService.reports(this.buildListParams(), role).subscribe((result) => {
			const data = result.data;
			const forms = data.data as TrainingForms[];

			const total: number = data.total;

			this.forms = forms.map((form) => ({
				...form,
				formTypeLabel: this.formTypeLabel(form.formTypeCode),
			}));

			const paginationDetails = this.calculatePages(total, this.size);

			this.pagination = {
				limit: this.size,
				offset: this.page,
				count: this.size * paginationDetails.totalPages,
			};

			this.cdr.detectChanges();

			this.loaderService.setLoading(false);
		});
	}

	onTableChanged(event: any) {
		const eventName = event.event;

		switch (eventName) {
			case "onPagination":
				const value = event.value;

				this.updateFilters({
					...this.filters,
					page: value.page,
					size: value.limit,
					role: this.roleService.getUserRole(),
				});

				break;

			// other cases

			default:
				// do nothing
				break;
		}
	}

	handleDateRangeChange(dateRange: DateRange) {
		if (dateRange?.enabled === false) {
			this.dateFilterEnabled = false;
			this.updateFilters({
				...this.filters,
				startDate: undefined,
				endDate: undefined,
				page: 1,
				role: this.roleService.getUserRole(),
			});
			return;
		}

		if (dateRange?.startDate && dateRange?.endDate) {
			this.dateFilterEnabled = true;
			this.updateFilters({
				...this.filters,
				startDate: moment(dateRange.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT),
				endDate: moment(dateRange.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT),
				page: 1,
				role: this.roleService.getUserRole(),
			});
		}
	}

	updateFilters(filters: FormParams) {
		if (this.isGroupAdmin) {
			this.selectedAocIds = filters.aocIds?.length ? filters.aocIds : parseAocIdsFromQuery(filters.aocId);
		}

		const incomingFormTypes = filters?.formTypes;
		const nextFormTypes = Array.isArray(incomingFormTypes)
			? incomingFormTypes.map(String)
			: incomingFormTypes
				? [String(incomingFormTypes)]
				: this.formTypes;
		const searchText = Object.prototype.hasOwnProperty.call(filters, "search")
			? String(filters.search ?? "").trim()
			: (this.filters?.search ?? "");

		this.formTypes = nextFormTypes;
		if (Object.prototype.hasOwnProperty.call(filters, "search")) {
			this.searchQuery = String(filters.search ?? "");
		}
		this.filters = {
			page: filters.page ?? this.constant.PAGINATION.DEFAULT_PAGE,
			size: filters.size ?? this.constant.PAGINATION.DEFAULT_SIZE,
			role: filters.role ?? this.roleService.getUserRole(),
			...(filters.startDate ? { startDate: filters.startDate } : {}),
			...(filters.endDate ? { endDate: filters.endDate } : {}),
			...(nextFormTypes.length ? { formTypes: nextFormTypes } : {}),
			...(this.isGroupAdmin && this.selectedAocIds.length ? { aocIds: this.selectedAocIds } : {}),
			...(searchText ? { search: searchText } : {}),
		};

		if (!isEmpty(filters?.size)) {
			this.configuration.rows = parseInt(`${this.size}`);
		}

		if (!isEmpty(filters?.startDate)) {
			this.startDate = moment(filters.startDate).toDate();
		}

		if (!isEmpty(filters?.endDate)) {
			this.endDate = moment(filters.endDate).toDate();
		}

		this.updateUrl();
		this.initFormList();
	}

	updateUrl(): void {
		const queryParams: FormParams = {
			page: this.page,
			size: this.size,
			role: this.roleService.getUserRole(),
		};

		if (this.filters?.startDate) {
			queryParams.startDate = this.filters.startDate;
		}
		if (this.filters?.endDate) {
			queryParams.endDate = this.filters.endDate;
		}
		if (this.formTypes.length) {
			queryParams.formTypes = this.formTypes;
		}
		if (this.isGroupAdmin && this.selectedAocIds.length) {
			queryParams.aocIds = this.selectedAocIds;
		}
		if (this.filters?.search) {
			queryParams.search = this.filters.search;
		}

		const url = this.router.serializeUrl(
			this.router.createUrlTree([RouteType.REPORT], {
				queryParams,
			}),
		);

		this.location.go(url);
	}

	formatDate(dateString: string): string {
		return moment(dateString).format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
	}

	createNewForm(): void {
		this.router.navigate([RouteType.NEW_FORM]);
	}

	get constant() {
		return CONSTANT;
	}

	get page(): number {
		return parseInt(`${this.filters?.page ?? this.constant.PAGINATION.DEFAULT_PAGE}`);
	}

	get size(): number {
		return parseInt(`${this.filters?.size ?? this.constant.PAGINATION.DEFAULT_SIZE}`);
	}

	calculatePages(totalDataCount: number, rowsPerPage: number): PaginationDetails {
		const totalPages = Math.ceil(totalDataCount / rowsPerPage);
		const lastPageSize = totalDataCount % rowsPerPage;

		return { totalPages, lastPageSize };
	}

	formTypeLabel(formTypeCode?: string): string {
		return getFormTypeShortLabel(formTypeCode) || "-";
	}

	get isInstructor(): boolean {
		return this.roleService.hasInstructorView;
	}

	get isAdmin(): boolean {
		return this.roleService.isAdmin;
	}

	get isGroupAdmin(): boolean {
		return this.roleService.isGroupAdmin;
	}

	get showAdminFilters(): boolean {
		return this.isAdmin || this.isGroupAdmin;
	}

	view(rowIndex: number): void {
		// this.editRow = rowIndex;
		this.router.navigate([RouteType.REPORT, rowIndex]);
	}

	onFormTypeChanged(value: string[] = []) {
		this.updateFilters({
			...this.filters,
			formTypes: value,
			page: 1,
			role: this.roleService.getUserRole(),
		});
	}

	onAocFilterChanged(aocIds: string[]): void {
		this.selectedAocIds = aocIds ?? [];

		this.updateFilters({
			...this.filters,
			aocIds: this.selectedAocIds.length ? this.selectedAocIds : undefined,
			page: 1,
			role: this.roleService.getUserRole(),
		});
	}

	onSearch(): void {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
		this.searchTimeout = setTimeout(() => {
			this.updateFilters({
				...this.filters,
				search: this.searchQuery.trim(),
				page: 1,
				role: this.roleService.getUserRole(),
			});
		}, 300);
	}

	get filterOptions() {
		return filterOptionsByRole(this.sharedService.filterOptions, this.roleService.getUserRole());
	}

	get aocFilterOptions() {
		return this.sharedService.aocFilterOptions;
	}
}
